import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  Shield, 
  Code, 
  ArrowRight, 
  Calendar,
  Building
} from 'lucide-react'
import { caseStudies } from '../lib/data'

const CaseStudiesPage = () => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'security' | 'development'>('all')
  
  const filteredCaseStudies = activeFilter === 'all' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeFilter)

  return (
    <>
      <Helmet>
        <title>Case Studies - DevSecRoot</title>
        <meta name="description" content="Explore real-world examples of how DevSecRoot has helped clients achieve their security and development goals." />
        <link rel="canonical" href="https://devsecroot.com/case-studies" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              Case Studies
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Real-world examples of how we've helped clients achieve their security and development goals. 
              See the impact of our work in action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="flex justify-center mb-12">
            <div className="bg-dsr-card backdrop-blur-md border border-white/10 rounded-2xl p-2">
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeFilter === 'all'
                      ? 'bg-gradient-to-r from-dsr-accent to-cyan-500 text-black shadow-lg'
                      : 'text-dsr-text/70 hover:text-dsr-text'
                  }`}
                >
                  All Cases
                </button>
                <button
                  onClick={() => setActiveFilter('security')}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeFilter === 'security'
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg'
                      : 'text-dsr-text/70 hover:text-dsr-text'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4" />
                    <span>Security</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveFilter('development')}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeFilter === 'development'
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                      : 'text-dsr-text/70 hover:text-dsr-text'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Code className="w-4 h-4" />
                    <span>Development</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Case Studies Grid */}
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {filteredCaseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover overflow-hidden h-full flex flex-col"
              >
                <div className="h-48 bg-gradient-to-br from-dsr-accent/20 to-cyan-500/20 flex items-center justify-center">
                  <Shield className="w-16 h-16 text-dsr-accent" />
                </div>
                
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      study.category === 'security' 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {study.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-dsr-text mb-3">{study.title}</h3>
                  <p className="text-dsr-text/70 mb-4 flex-grow">{study.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm text-dsr-text/60">
                      <Building className="w-4 h-4" />
                      <span>{study.client}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-dsr-text/60">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(study.publishedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/case-studies/${study.slug}`}
                    className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors mt-auto"
                  >
                    <span>Read case study</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredCaseStudies.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-dsr-text/70 text-lg">
                No case studies found for the selected filter.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Ready to Create Your Success Story?
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              Let's work together to secure your applications and build something amazing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Start Your Project
              </Link>
              <Link to="/services" className="btn-secondary text-lg px-8 py-4">
                View Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default CaseStudiesPage
